/**
 * Shop Module
 *
 * Handles variant selection, price updates, and thumbnail navigation
 * on product detail pages. Loaded as part of the shop bundle (shop.min.js).
 */

/**
 * Initialize variant selector — grouped options or flat buttons
 */
function initVariantSelector() {
  const container = document.querySelector("[data-variants]");
  if (!container) {
    return;
  }

  const optionGroups = container.querySelectorAll("[data-option-group]");
  if (optionGroups.length > 0) {
    initGroupedVariants(container, optionGroups);
    return;
  }

  initFlatVariants(container);
}

function initFlatVariants(container) {
  const buttons = container.querySelectorAll("[data-variant-btn]");
  if (buttons.length === 0) {
    return;
  }

  const priceEl = document.querySelector("[data-price]");

  buttons.forEach((btn) => {
    btn.addEventListener("click", () => {
      if (btn.disabled) {
        return;
      }

      buttons.forEach((b) => b.classList.remove("shop-product__variant-btn--active"));
      btn.classList.add("shop-product__variant-btn--active");

      if (priceEl) {
        priceEl.textContent = btn.dataset.price;
      }

      dispatchVariantChange(container, {
        variantId: btn.dataset.variantId,
        price: btn.dataset.price,
        available: btn.dataset.available === "true",
        versand: btn.dataset.versand || "",
      });
    });
  });
}

/**
 * Read variant entries from hidden inputs in the DOM
 */
function readVariantEntries(container) {
  const entries = container.querySelectorAll("[data-variant-entry]");
  return Array.from(entries).map((el) => {
    const options = {};
    for (let i = 1; i <= 3; i++) {
      const val = el.dataset[`option${i}`];
      if (val !== undefined) {
        options[i] = val;
      }
    }
    return {
      id: el.dataset.variantId,
      price: el.dataset.price,
      available: el.dataset.available === "true",
      quantity: el.dataset.quantity !== undefined ? parseInt(el.dataset.quantity, 10) : null,
      imageIndex: el.dataset.imageIndex !== undefined ? parseInt(el.dataset.imageIndex, 10) : -1,
      versand: el.dataset.versand || "",
      options,
    };
  });
}



function initGroupedVariants(container, optionGroups) {
  const priceEl = document.querySelector("[data-price]");
  const variants = readVariantEntries(container);

  // Current selection: { 1: "6m", 2: "Kein Bundle" }
  const selection = {};
  if (variants.length > 0) {
    for (const [idx, val] of Object.entries(variants[0].options)) {
      selection[idx] = val;
    }
  }

  // Attach click handlers to pre-rendered buttons
  optionGroups.forEach((group, groupIdx) => {
    const optIdx = String(groupIdx + 1);
    const buttons = group.querySelectorAll("[data-option-value]");

    buttons.forEach((btn) => {
      btn.addEventListener("click", () => {
        if (btn.disabled) {
          return;
        }

        selection[optIdx] = btn.dataset.optionValue;

        // Update active in this group
        buttons.forEach((b) => b.classList.remove("shop-product__variant-btn--active"));
        btn.classList.add("shop-product__variant-btn--active");

        // Find matching variant
        const matched = findVariant(variants, selection);
        if (matched) {
          if (priceEl) {
            priceEl.textContent = matched.price;
          }
          dispatchVariantChange(container, {
            variantId: matched.id,
            price: matched.price,
            available: matched.available,
            imageIndex: matched.imageIndex,
            versand: matched.versand,
          });
        }

        updateOptionAvailability(variants, selection, optionGroups);
      });
    });
  });

  // Initial availability
  updateOptionAvailability(variants, selection, optionGroups);

  // Dispatch initial variant
  const initial = findVariant(variants, selection);
  if (initial) {
    dispatchVariantChange(container, {
      variantId: initial.id,
      price: initial.price,
      available: initial.available,
      imageIndex: initial.imageIndex,
      versand: initial.versand,
    });
  }
}

function findVariant(variants, selection) {
  return variants.find((v) =>
    Object.entries(selection).every(([idx, val]) => v.options[idx] === val),
  );
}

function updateOptionAvailability(variants, selection, optionGroups) {
  optionGroups.forEach((group, groupIdx) => {
    const optIdx = String(groupIdx + 1);
    const buttons = group.querySelectorAll("[data-option-value]");

    buttons.forEach((btn) => {
      const testSelection = { ...selection, [optIdx]: btn.dataset.optionValue };
      const match = findVariant(variants, testSelection);
      const isAvailable = match && match.available;

      btn.classList.toggle("shop-product__variant-btn--unavailable", !isAvailable);
      btn.disabled = !match;

      // Wrap text in a span for truncation (once)
      if (!btn.querySelector(".shop-product__variant-btn-text")) {
        const textSpan = document.createElement("span");
        textSpan.className = "shop-product__variant-btn-text";
        // Move all text nodes into the span
        while (btn.firstChild && !btn.firstChild.classList) {
          textSpan.appendChild(btn.firstChild);
        }
        btn.prepend(textSpan);
      }

    });
  });
}

function dispatchVariantChange(container, detail) {
  container.dispatchEvent(
    new CustomEvent("variantchange", {
      bubbles: true,
      detail,
    }),
  );
}



/**
 * Initialize thumbnail navigation to sync with image slider
 */
function initThumbnails() {
  const thumbs = document.querySelectorAll("[data-slide-index]");
  if (thumbs.length === 0) {
    return;
  }

  const slider = document.querySelector(".shop-product__gallery .image-slider");
  if (!slider) {
    return;
  }

  const track = slider.querySelector(".image-slider__track");
  if (!track) {
    return;
  }

  function goToSlide(index) {
    track.style.transform = `translateX(-${100 * index}%)`;
    // Update thumbnails
    thumbs.forEach((t) =>
      t.classList.remove("shop-product__thumb--active"),
    );
    if (thumbs[index]) {
      thumbs[index].classList.add("shop-product__thumb--active");
    }
  }

  thumbs.forEach((thumb) => {
    thumb.addEventListener("click", () => {
      const index = parseInt(thumb.dataset.slideIndex, 10);
      goToSlide(index);
    });
  });

  // Sync thumbnails when arrows/swipe change the slide
  const observer = new MutationObserver(() => {
    const transform = track.style.transform;
    const match = transform.match(/translateX\(-(\d+)%\)/);
    if (match) {
      const index = parseInt(match[1], 10) / 100;
      thumbs.forEach((t) =>
        t.classList.remove("shop-product__thumb--active"),
      );
      if (thumbs[index]) {
        thumbs[index].classList.add("shop-product__thumb--active");
      }
    }
  });

  observer.observe(track, {
    attributes: true,
    attributeFilter: ["style"],
  });
}

/**
 * Initialize share buttons (copy link with clipboard feedback)
 */
function initShare() {
  const copyBtn = document.querySelector("[data-share-copy]");
  if (!copyBtn) {
    return;
  }

  copyBtn.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      const svg = copyBtn.innerHTML;
      copyBtn.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';
      copyBtn.style.borderColor = "#4ade80";
      copyBtn.style.color = "#4ade80";
      setTimeout(() => {
        copyBtn.innerHTML = svg;
        copyBtn.style.borderColor = "";
        copyBtn.style.color = "";
      }, 2000);
    } catch {
      // Fallback: do nothing
    }
  });
}

/**
 * Shared gallery slider control — reads/writes the track transform directly
 */
function createGalleryControl() {
  const gallery = document.querySelector(".shop-product__gallery-main");
  if (!gallery) {
    return null;
  }

  const track = gallery.querySelector(".image-slider__track");
  const slides = gallery.querySelectorAll(".image-slider__slide");
  if (!track || slides.length === 0) {
    return null;
  }

  const total = slides.length;

  let _currentIndex = 0;

  function getIndex() {
    return _currentIndex;
  }

  function goTo(index) {
    _currentIndex = index;
    track.style.transform = `translateX(-${100 * index}%)`;
  }

  // Sync index when external code (image-slider.js) moves the track
  const trackObserver = new MutationObserver(() => {
    const match = track.style.transform.match(/translateX\(-(\d+)%\)/);
    if (match) {
      _currentIndex = parseInt(match[1], 10) / 100;
    }
  });
  trackObserver.observe(track, { attributes: true, attributeFilter: ["style"] });

  return { track, total, getIndex, goTo };
}

/**
 * Initialize gallery prev/next arrow buttons
 */
function initGalleryArrows() {
  const ctrl = createGalleryControl();
  if (!ctrl) {
    return;
  }

  const gallery = document.querySelector(".shop-product__gallery-main");
  const prevBtn = gallery.querySelector("[data-gallery-prev]");
  const nextBtn = gallery.querySelector("[data-gallery-next]");
  if (!prevBtn || !nextBtn) {
    return;
  }

  prevBtn.addEventListener("click", () => {
    const current = ctrl.getIndex();
    ctrl.goTo(current > 0 ? current - 1 : ctrl.total - 1);
  });

  nextBtn.addEventListener("click", () => {
    const current = ctrl.getIndex();
    ctrl.goTo(current < ctrl.total - 1 ? current + 1 : 0);
  });
}

/**
 * Initialize gallery counter that shows "1 / N" and syncs with slider
 */
function initGalleryCounter() {
  const ctrl = createGalleryControl();
  if (!ctrl) {
    return;
  }

  const gallery = document.querySelector(".shop-product__gallery-main");
  const counter = gallery.querySelector("[data-gallery-counter]");
  if (!counter) {
    return;
  }

  function updateCounter() {
    counter.textContent = `${ctrl.getIndex() + 1} / ${ctrl.total}`;
  }

  updateCounter();
  const observer = new MutationObserver(updateCounter);
  observer.observe(ctrl.track, {
    attributes: true,
    attributeFilter: ["style"],
  });
}

/**
 * Initialize lightbox for product images
 */
function initLightbox() {
  const gallery = document.querySelector(".shop-product__gallery-main");
  if (!gallery) {
    return;
  }

  const ctrl = createGalleryControl();
  const viewport = gallery.querySelector(".image-slider__viewport");
  const slides = gallery.querySelectorAll(".image-slider__slide img");
  if (!ctrl || !viewport || slides.length === 0) {
    return;
  }

  let lightbox = null;
  let lightboxImg = null;
  let lightboxCounter = null;
  let currentIndex = 0;
  const total = slides.length;

  function showImage(index) {
    currentIndex = (index + total) % total;
    lightboxImg.src = slides[currentIndex].src;
    lightboxImg.alt = slides[currentIndex].alt;
    if (lightboxCounter) {
      lightboxCounter.textContent = `${currentIndex + 1} / ${total}`;
    }
    // Sync main slider
    ctrl.goTo(currentIndex);
  }

  function createLightbox() {
    const lang = (window.VANYX_CONFIG || {}).lang || "de";
    lightbox = document.createElement("div");
    lightbox.className = "shop-lightbox";
    lightbox.setAttribute("role", "dialog");
    lightbox.setAttribute("aria-label", lang === "en" ? "Image lightbox" : "Bildansicht");
    lightbox.innerHTML = `
      <button type="button" class="shop-lightbox__close" aria-label="${lang === "en" ? "Close" : "Schließen"}">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
      </button>
      ${total > 1 ? `
      <button type="button" class="shop-lightbox__arrow shop-lightbox__arrow--prev" aria-label="${lang === "en" ? "Previous" : "Zurück"}">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
      </button>
      <button type="button" class="shop-lightbox__arrow shop-lightbox__arrow--next" aria-label="${lang === "en" ? "Next" : "Weiter"}">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
      </button>
      <span class="shop-lightbox__counter"></span>
      ` : ""}
      <img class="shop-lightbox__image" src="" alt="" />
    `;

    document.body.appendChild(lightbox);

    lightboxImg = lightbox.querySelector(".shop-lightbox__image");
    lightboxCounter = lightbox.querySelector(".shop-lightbox__counter");

    // Close handlers
    lightbox.querySelector(".shop-lightbox__close").addEventListener("click", closeLightbox);
    lightbox.addEventListener("click", (e) => {
      if (e.target === lightbox) {
        closeLightbox();
      }
    });

    // Arrow handlers
    if (total > 1) {
      lightbox.querySelector(".shop-lightbox__arrow--prev").addEventListener("click", () => showImage(currentIndex - 1));
      lightbox.querySelector(".shop-lightbox__arrow--next").addEventListener("click", () => showImage(currentIndex + 1));
    }

    // Swipe support
    let touchStartX = 0;
    lightbox.addEventListener("touchstart", (e) => {
      touchStartX = e.changedTouches[0].clientX;
    }, { passive: true });

    lightbox.addEventListener("touchend", (e) => {
      const dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) > 50) {
        if (dx > 0) {
          showImage(currentIndex - 1);
        } else {
          showImage(currentIndex + 1);
        }
      }
    });
  }

  function openLightbox() {
    if (!lightbox) {
      createLightbox();
    }
    currentIndex = ctrl.getIndex();
    showImage(currentIndex);
    lightbox.style.display = "flex";
    // Force reflow for transition
    void lightbox.offsetHeight;
    lightbox.classList.add("is-active");
    document.body.style.overflow = "hidden";
  }

  function closeLightbox() {
    if (!lightbox) {
      return;
    }
    lightbox.classList.remove("is-active");
    setTimeout(() => {
      lightbox.style.display = "none";
      document.body.style.overflow = "";
    }, 250);
  }

  // Open on viewport click
  viewport.addEventListener("click", openLightbox);

  // Keyboard navigation
  document.addEventListener("keydown", (e) => {
    if (!lightbox || lightbox.style.display === "none") {
      return;
    }
    if (e.key === "Escape") {
      closeLightbox();
    } else if (e.key === "ArrowLeft") {
      showImage(currentIndex - 1);
    } else if (e.key === "ArrowRight") {
      showImage(currentIndex + 1);
    }
  });
}

/**
 * Initialize hover-zoom on product gallery (desktop only)
 */
function initGalleryZoom() {
  const gallery = document.querySelector(".shop-product__gallery-main");
  if (!gallery) {
    return;
  }

  const viewport = gallery.querySelector(".image-slider__viewport");
  if (!viewport) {
    return;
  }

  // Only on devices with hover (no touch)
  if (!window.matchMedia("(hover: hover)").matches) {
    return;
  }

  const track = viewport.querySelector(".image-slider__track");

  viewport.addEventListener("mousemove", (e) => {
    const rect = viewport.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    track.style.transformOrigin = `${x}% ${y}%`;
    track.style.transform = "scale(1.8)";
  });

  viewport.addEventListener("mouseleave", () => {
    track.style.transformOrigin = "";
    track.style.transform = "";
  });
}

/**
 * Initialize question modal (open/close)
 */
function initQuestionModal() {
  const openBtn = document.querySelector("[data-question-open]");
  const modal = document.querySelector("[data-question-modal]");
  if (!openBtn || !modal) {
    return;
  }

  function open() {
    modal.hidden = false;
    document.body.style.overflow = "hidden";
  }

  function close() {
    modal.hidden = true;
    document.body.style.overflow = "";
  }

  openBtn.addEventListener("click", open);

  modal.querySelectorAll("[data-question-close]").forEach((el) => {
    el.addEventListener("click", close);
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !modal.hidden) {
      close();
    }
  });
}

/**
 * Sticky add-to-cart bar on mobile — appears when main CTA scrolls out of view
 */
function initStickyATC() {
  const addBtn = document.querySelector("[data-add-to-cart]");
  if (!addBtn) {
    return;
  }

  // Only on mobile/tablet
  if (window.matchMedia("(min-width: 1024px)").matches) {
    return;
  }

  const priceEl = document.querySelector("[data-price]");
  const currencyEl = document.querySelector(".shop-product__price-currency");

  const bar = document.createElement("div");
  bar.className = "shop-sticky-atc";
  bar.innerHTML = `
    <span class="shop-sticky-atc__price">
      <span data-sticky-price>${priceEl ? priceEl.textContent : ""}</span>
      <span>${currencyEl ? currencyEl.textContent : "€"}</span>
    </span>
    <button type="button" class="button button--primary shop-sticky-atc__btn" data-sticky-add>
      <span style="color:#fff">${addBtn.querySelector("span").textContent}</span>
    </button>
  `;
  document.body.appendChild(bar);

  // Sync price on variant change
  const variants = document.querySelector("[data-variants]");
  if (variants) {
    variants.addEventListener("variantchange", (e) => {
      const stickyPrice = bar.querySelector("[data-sticky-price]");
      if (stickyPrice) {
        stickyPrice.textContent = e.detail.price;
      }
    });
  }

  // Click triggers the main add-to-cart button
  bar.querySelector("[data-sticky-add]").addEventListener("click", () => {
    addBtn.click();
  });

  // Show/hide based on main CTA visibility
  const observer = new IntersectionObserver(
    ([entry]) => {
      bar.classList.toggle("is-visible", !entry.isIntersecting);
    },
    { threshold: 0 },
  );
  observer.observe(addBtn);
}

/**
 * Track GA4 view_item event on product pages
 */
function trackViewItem() {
  const productEl = document.querySelector("[data-shopify-handle]");
  if (!productEl) {
    return;
  }

  const priceEl = document.querySelector("[data-price]");
  const titleEl = document.querySelector(".shop-product__title");
  if (!priceEl || !titleEl) {
    return;
  }

  const price = parseFloat(priceEl.textContent.replace(/[^\d.,]/g, "").replace(/\./g, "").replace(",", "."));
  if (typeof window.gtag === "function") {
    window.gtag("event", "view_item", {
      currency: "EUR",
      value: price,
      items: [{
        item_id: productEl.dataset.shopifyHandle,
        item_name: titleEl.textContent.trim(),
        price: price,
        currency: "EUR",
      }],
    });
  }
}

/**
 * Navigate gallery to variant image on variant change
 */
function initVariantImageNav() {
  const container = document.querySelector("[data-variants]");
  if (!container) {
    return;
  }

  const ctrl = createGalleryControl();
  if (!ctrl) {
    return;
  }

  container.addEventListener("variantchange", (e) => {
    const idx = e.detail.imageIndex;
    if (idx !== undefined && idx >= 0 && idx < ctrl.total) {
      ctrl.goTo(idx);
    }
  });
}

/**
 * Shipping tier → price mapping
 */
const SHIPPING_PRICES = {
  "Kleinpaket": { de: "4,90 €", en: "€4.90" },
  "Mittelpaket": { de: "12,90 €", en: "€12.90" },
  "Sperrgut (klein)": { de: "49,90 €", en: "€49.90" },
  "Sperrgut (groß)": { de: "169,00 €", en: "€169.00" },
};

/**
 * Update trust bar and shipping accordion when variant changes
 */
function initShippingUpdate() {
  const container = document.querySelector("[data-variants]");
  if (!container) {
    return;
  }

  const costEl = document.querySelector("[data-shipping-cost]");
  const lang = (window.VANYX_CONFIG || {}).lang || "de";
  const shippingPageUrl = costEl ? costEl.querySelector("a")?.href : "";

  container.addEventListener("variantchange", (e) => {
    if (!costEl) {
      return;
    }
    const tier = e.detail.versand;
    const priceEntry = tier ? SHIPPING_PRICES[tier] : null;
    const link = `<a href="${shippingPageUrl}">${lang === "en" ? "details" : "Details"}</a>`;

    if (priceEntry) {
      costEl.innerHTML = lang === "en"
        ? `Shipping ${priceEntry.en} · ${link}`
        : `Versand ${priceEntry.de} · ${link}`;
    } else {
      costEl.innerHTML = lang === "en"
        ? `Shipping from €4.90 · ${link}`
        : `Versand ab 4,90 € · ${link}`;
    }
  });
}

/**
 * Initialize all shop functionality
 */
export function init() {
  initShippingUpdate();
  initVariantSelector();
  initThumbnails();
  initGalleryArrows();
  initGalleryCounter();
  initLightbox();
  initGalleryZoom();
  initQuestionModal();
  initShare();
  initStickyATC();
  initVariantImageNav();
  trackViewItem();
}
